/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  MouseEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Button from '@material-ui/core/Button';
import {ColDef, ColumnState} from 'ag-grid-community';
import isEmpty from 'lodash.isempty';
import {JobTaskOrder} from '@ion/applications/src/components/jobs/JobTaskOrder';
import {exportDownloadJobsStatus} from '@ion/applications/src/store/actions/exporter';
import {translate} from '@ion/core/src/locale/locale';
import {parseDownloadJobs} from '@ion/core/src/services/ConnectUtils';
import {
  CRUDPermissions,
  DownloadJob, DownloadJobType
} from '@ion/core/src/types/entities';
import {formatDateTime, timeDiff} from '@ion/core/src/utils/DateUtil';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {
  getPinnedRowActionColumnDefinition
} from '@ion/dls/src/grid/IonGridUtils';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  useColumnChooser
} from '@ion/dls/src/grid/useColumnChooser';
import {
  useColumnState
} from '@ion/dls/src/grid/useColumnState';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {GridRefreshIcon} from '@ion/dls/src/icons/GridRefreshIcon';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  cancelTasks,
  showDevicesStatus
} from '@/store/actions/downloadjobs';
import CancelTask from './CancelTask';
import {getTaskOrder} from './JobsGridUtils';
import ViewDevicesStatus from './ViewDevicesStatus';

export interface DownloadJobStatusGridProps {
  downloadjobs?: DownloadJob;
  refreshPage?: boolean;
  setRefreshPage?: Function;
  onRefresh?: MouseEventHandler<HTMLButtonElement>;
  gridRefresh?: boolean;
  jobsPermissions?: CRUDPermissions;
  dialogClose?: Function;
}

// Styles for the Job Container
const useStyles = (theme: DLSTheme) => ({
  searchField : {
    width                          : '40%',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px',
      width       : '100%'
    }
  },
  filterSection : {
    display        : 'flex',
    padding        : '7px 1px',
    justifyContent : 'flex-end'
  },
  formIcons : {
    display        : 'flex',
    justifyContent : 'flex-end',
    alignItems     : 'revert',
    marginRight    : '-8px'
  },
  refreshButtonRoot : {
    height    : '41px',
    marginTop : '5px',
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  },
  exportIcon : {
    display        : 'flex',
    justifyContent : 'end'
  },
  downloadJobsStatusGrid : {
    height : '30vh !important'
  }
});

/**
 * Grid to be rendered on the Provisioning Jobs page.
 */
export const DownloadJobStatusGrid = ({
  downloadjobs,
  setRefreshPage,
  jobsPermissions,
  onRefresh,
  dialogClose,
  refreshPage = false,
  gridRefresh = false
}: DownloadJobStatusGridProps, ref) => {
  const dispatch                                   = useDispatch();
  const containerStyles                            = useExtendStyles(useStyles);
  const classes                                    = containerStyles();
  const columnState: ColumnState[]                 = useSelector(({downloadjobs: {grid}}: DownloadJobStatusGridProps) =>
    grid.columnState);
  const taskRef                                    = useRef<IonGridAPI>();
  const [refreshRequired, setRefreshRequired]      = useState(refreshPage);
  const {definitions, defaultDefinitions}          = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState
  });
  const {columnDefinitions}                        = useColumnChooser(definitions);
  const [devicesStatus, setDevicesStatus]          = useState(false);
  const [indexValue, setIndexValue]                = useState(null);
  const [showStatusPrompt, setShowStatusPrompt]    = useState<boolean>(false);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    onExport,
    onRefresh
  }));

  useEffect(() => {
    setRefreshPage(refreshPage);
  }, [refreshPage]);

  useEffect(() => {
    setRefreshRequired(refreshPage);
    setRefreshPage(false);
    return function() {
      dispatch(cancelTasks(null, null, false));
    };
  }, [refreshPage]);

  const onExport = function() {
    const params = taskRef.current?.getExportParams(false);
    dispatch(exportDownloadJobsStatus(params, downloadjobs));
  };

  const getDevicesJobs = useCallback(({
    callback: callbackFn
  }) => {
    if (!isEmpty(downloadjobs)) {
      const results = parseDownloadJobs(downloadjobs);
      callbackFn?.({items: results, totalCount: results.length});
      return {items: results, totalCount: results.length};
    }
  }, [downloadjobs]);

  const onDevicesStatus = (index) => {
    const createdDate = formatDateTime(downloadjobs?.createdOn, true, true);
    const currentDate = formatDateTime(Date(), true, true);
    const secDiff     = timeDiff(currentDate, createdDate, 'seconds');
    if (secDiff <= 15) {
      setShowStatusPrompt(true);
    } else {
      dispatch(showDevicesStatus(downloadjobs, true));
      setDevicesStatus(true);
      setIndexValue(index);
    }
  };

  const onDismiss = useCallback(() => {
    setShowStatusPrompt(false);
  }, []);

  return (
    <>
      <div className={classes.filterSection}>
        <div className={classes.formIcons}>
          <Button onClick={onRefresh}
            className={classes.refreshButtonRoot}
            disableRipple>
            <GridRefreshIcon />
          </Button>
        </div>
        <div className={classes.exportIcon}>
          <CSVExportButton onExport={onExport} />
        </div>
      </div>
      <IonGrid omitHeader
        ref={taskRef}
        refreshRequired={refreshRequired || gridRefresh}
        setRefreshRequired={setRefreshRequired}
        loadItems={getDevicesJobs}
        augmentItems={constructDownloadJobStatusGridRows}
        getGridHeight={function() {
          return classes.downloadJobsStatusGrid;
        }}
        columnComponents={{
          devicesStatus : (item: any) => (
            item?.data?.total > 0 ? <LinkCell data={item}
              onClick={function() {
                onDevicesStatus(getTaskOrder(item));
              }} /> : 0
          ),
          cancelTask : item => (
            <CancelTask task={item?.data}
              jobsPermissions={jobsPermissions}
              index={getTaskOrder(item)}
              downloadjobs={downloadjobs} />
          ),
          taskOrderCell : (item) => (
            <JobTaskOrder taskOrder={getTaskOrder(item, false)}
              taskEndOrder={downloadjobs?.tasks?.length}
              isJobView />
          )
        }}
        rowMenusEnabled={false}
        checkboxesEnabled={false}
        columnDefinitions={columnDefinitions}
        defaultDefinitions={defaultDefinitions} />
      {devicesStatus && (
        <ViewDevicesStatus open={devicesStatus}
          jobsPermissions={jobsPermissions}
          deviceStatus={setDevicesStatus}
          downloadjobs={downloadjobs}
          jobStatusDialogClose={dialogClose}
          index={indexValue} />
      )}
      {showStatusPrompt && (
        <UserNotificationDialog show
          capitalize
          title={downloadjobs?.name}
          message={translate('downloadjobs:jobStatus.devicesCountStatus')}
          dismissLabel={translate('common:ok')}
          onDismiss={onDismiss}/>
      )}
    </>
  );
};

/**
 * Converts a collection of jobs and converts them to a list of items specific
 * for display within the Provisioning Jobs Grid, while retaining all of the items existing
 * attributes,
 */
export const constructDownloadJobStatusGridRows = (downloadJobs: DownloadJob[]): GridResult[] => {
  const statusInProgress = translate('common:jobStatus.IN_PROGRESS');
  return downloadJobs.map(downloadJob => {
    const {
      progressStatus
    } = downloadJob;
    const isInProgress = progressStatus === DownloadJobType.Inprogress;
    return {
      ...downloadJob,
      progressStatus : isInProgress ? statusInProgress : progressStatus
    };
  });
};

/**
 *
 * Returns column definitions for the desktop grid implementation ...
 */
const getColumnDefinitionDesktop = (): ColDef[] => {
  return [{
    headerName         : translate('downloadjobs:jobStatus.taskOrder'),
    field              : 'taskOrder',
    cellRenderer       : 'taskOrderCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }, {
    headerName : translate('downloadjobs:jobStatus.task'),
    field      : 'task',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.taskDescription'),
    field      : 'description',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName         : translate('downloadjobs:jobStatus.devicesCount'),
    field              : 'total',
    cellRenderer       : 'devicesStatus',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }, {
    headerName : translate('downloadjobs:jobStatus.completed'),
    field      : 'completed',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.pending'),
    field      : 'pending',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.failed'),
    field      : 'failed',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.cancelled'),
    field      : 'cancelled',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.progressStatus'),
    field      : 'progressStatus',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, getPinnedRowActionColumnDefinition('cancelTask')];
};

/**
 *
 * Returns column definitions for mobile grid implementation ...
 */
const getColumnDefinitionMobile = (): ColDef[] => {
  return [{
    headerName         : translate('downloadjobs:jobStatus.taskOrder'),
    field              : 'taskOrder',
    cellRenderer       : 'taskOrderCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }, {
    headerName : translate('downloadjobs:jobStatus.task'),
    field      : 'task',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.taskDescription'),
    field      : 'description',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName         : translate('downloadjobs:jobStatus.devicesCount'),
    field              : 'total',
    cellRenderer       : 'devicesStatus',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }, {
    headerName : translate('downloadjobs:jobStatus.completed'),
    field      : 'completed',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.pending'),
    field      : 'pending',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.failed'),
    field      : 'failed',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.cancelled'),
    field      : 'cancelled',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:jobStatus.progressStatus'),
    field      : 'progressStatus',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, getPinnedRowActionColumnDefinition('cancelTask')];
};

const getColumnDefinitions = () => ({
  desktop : getColumnDefinitionDesktop(),
  mobile  : getColumnDefinitionMobile()
});

export default forwardRef(DownloadJobStatusGrid);
